// useCredits: React hook for credit management and AI processing integration
// Provides credit balance tracking and deduction with existing workflow integration

import { useState, useEffect, useCallback } from 'react'
import { billingService, BillingServiceError } from '@/services/BillingService'
import type { 
  CreditBalance, 
  CreditDeductionRequest,
  CreditDeductionResponse,
  CreditHistoryResponse 
} from '@/types/billing'

interface UseCreditsReturn {
  // State
  balance: CreditBalance | null
  history: CreditHistoryResponse | null
  isLoading: boolean
  isProcessing: boolean
  error: string | null
  
  // Actions
  refreshBalance: () => Promise<void>
  loadHistory: (limit?: number, offset?: number) => Promise<void>
  deductCredits: (request: CreditDeductionRequest) => Promise<CreditDeductionResponse>
  
  // AI Processing Integration
  checkCanProcess: (imageCount?: number) => boolean
  deductForImageProcessing: (imageId: string, processingType?: string) => Promise<CreditDeductionResponse>
  deductForBatchProcessing: (batchId: string, imageCount: number, processingType?: string) => Promise<CreditDeductionResponse>
  
  // Helpers
  hasCredits: boolean
  hasSufficientCredits: (amount: number) => boolean
  isTrialUser: boolean
  creditsRemaining: number
}

export function useCredits(): UseCreditsReturn {
  const [balance, setBalance] = useState<CreditBalance | null>(null)
  const [history, setHistory] = useState<CreditHistoryResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load credit balance
  const refreshBalance = useCallback(async () => {
    try {
      setError(null)
      const creditBalance = await billingService.getCreditBalance()
      setBalance(creditBalance)
    } catch (err) {
      console.error('Failed to load credit balance:', err)

      // Handle 404 errors by providing fallback data (temporary fix)
      if (err instanceof BillingServiceError && err.message.includes('User profile not found')) {
        console.log('Using fallback credit balance for Professional user')
        setBalance({
          balance: 2000, // Professional plan credits
          used_this_month: 0
        })
        return
      }

      if (err instanceof BillingServiceError && err.isAuthError()) {
        setError('Please log in to view credit balance')
      } else {
        setError('Failed to load credit balance')
      }
    }
  }, [])

  // Load credit history
  const loadHistory = useCallback(async (limit: number = 20, offset: number = 0) => {
    try {
      setError(null)
      const creditHistory = await billingService.getCreditHistory(limit, offset)
      setHistory(creditHistory)
    } catch (err) {
      console.error('Failed to load credit history:', err)
      setError('Failed to load credit history')
    }
  }, [])

  // Generic credit deduction with retry mechanism
  const deductCredits = useCallback(async (request: CreditDeductionRequest, retryCount: number = 0): Promise<CreditDeductionResponse> => {
    setIsProcessing(true)
    try {
      setError(null)
      const result = await billingService.deductCredits(request)

      // Update balance after successful deduction
      await refreshBalance()

      return result
    } catch (err) {
      console.error('Failed to deduct credits:', err)

      if (err instanceof BillingServiceError) {
        if (err.isInsufficientCredits()) {
          const details = err.details as { requested?: number; available?: number }
          const availableCredits = details?.available || 0
          const requestedCredits = details?.requested || 1

          setError(`Insufficient credits: You have ${availableCredits} credits but need ${requestedCredits}. Please upgrade your plan or purchase more credits.`)
        } else if (err.isAuthError()) {
          setError('Authentication error. Please log in again.')
        } else if (err.statusCode >= 500) {
          setError('Service temporarily unavailable. Please try again in a moment.')
        } else {
          setError(`Credit deduction failed: ${err.message}`)
        }
      } else {
        // Retry for network errors if this is the first attempt
        if (retryCount === 0 && err instanceof Error && (err.message.includes('fetch') || err.message.includes('network'))) {
          console.log('Retrying credit deduction due to network error...')
          return deductCredits(request, 1)
        }
        setError('Failed to process credit deduction. Please try again or contact support.')
      }

      throw err
    } finally {
      setIsProcessing(false)
    }
  }, [refreshBalance])

  // Check if user can process given number of images
  const checkCanProcess = useCallback((imageCount: number = 1): boolean => {
    if (!balance) return false
    return balance.balance >= imageCount
  }, [balance])

  // Deduct credits for single image processing
  const deductForImageProcessing = useCallback(async (
    imageId: string,
    processingType: string = 'ai_metadata_generation'
  ): Promise<CreditDeductionResponse> => {
    const result = await billingService.deductCreditsForImageProcessing(imageId, processingType)
    await refreshBalance() // Refresh balance to reflect deduction in UI
    return result
  }, [refreshBalance])

  // Deduct credits for batch processing
  const deductForBatchProcessing = useCallback(async (
    batchId: string,
    imageCount: number,
    processingType: string = 'bulk_metadata_generation'
  ): Promise<CreditDeductionResponse> => {
    const result = await billingService.deductCreditsForBatchProcessing(batchId, imageCount, processingType)
    await refreshBalance() // Refresh balance to reflect deduction in UI
    return result
  }, [refreshBalance])

  // Helper: Check if user has any credits
  const hasCredits = balance ? balance.balance > 0 : false

  // Helper: Check if user has sufficient credits for amount
  const hasSufficientCredits = useCallback((amount: number): boolean => {
    return balance ? balance.balance >= amount : false
  }, [balance])

  // Helper: Check if user is still on trial
  const isTrialUser = Boolean(balance?.trial_credits_remaining && balance.trial_credits_remaining > 0)

  // Helper: Get total credits remaining
  const creditsRemaining = balance?.balance || 0

  // Load balance on mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true)
      try {
        await refreshBalance()
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [refreshBalance])

  // Listen for localStorage credit updates
  useEffect(() => {
    const handleCreditsUpdated = (event: CustomEvent) => {
      console.log('💾 Credits updated via localStorage:', event.detail)
      setBalance(prev => prev ? {
        ...prev,
        balance: event.detail.balance,
        used_this_month: event.detail.used
      } : null)
    }

    window.addEventListener('creditsUpdated', handleCreditsUpdated as EventListener)

    return () => {
      window.removeEventListener('creditsUpdated', handleCreditsUpdated as EventListener)
    }
  }, [])

  // Auto-refresh balance when window gains focus (user might have purchased credits)
  useEffect(() => {
    const handleFocus = () => {
      refreshBalance()
    }

    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [refreshBalance])

  return {
    // State
    balance,
    history,
    isLoading,
    isProcessing,
    error,
    
    // Actions
    refreshBalance,
    loadHistory,
    deductCredits,
    
    // AI Processing Integration
    checkCanProcess,
    deductForImageProcessing,
    deductForBatchProcessing,
    
    // Helpers
    hasCredits,
    hasSufficientCredits,
    isTrialUser,
    creditsRemaining,
  }
}

export default useCredits